import { t } from "@hakili/i18n";
import {
  BANK_FONT,
  BANK_FS_BODY,
  BANK_FS_READ,
  BANK_TEXT,
  BANK_TEXT_2,
  BANK_TEXT_BAY,
  BANK_TEXT_BAY_2,
  HakiliMark,
  HakiliMascot,
  LIEN_TEXTE,
  classes,
} from "@hakili/ui";
import { Lock } from "lucide-react";
import type { ReactNode } from "react";

/**
 * `SIGNIN-VEILLEUR` (designer's row, D2 `screens.css:42`): **`.lcard h1{font:600 30px var(--hk-font)}`** (line-height `normal`, so 39 px) and
 * **`.lcard .sub`** (no size of its own: the body's 15px / 1.5, `components.css:5`). One pair for every card page that stands before the session.
 */
export const LOGIN_TITLE = "text-[30px] font-semibold leading-[normal]";
export const LOGIN_SUB = classes(BANK_FS_READ, BANK_TEXT_2);
/**
 * D2 `screens.css:43`: **`.lf{display:grid;gap:14px;margin-top:10px}`**, and the card above it is `gap:14px` (`:42`), so the title, the sub-line, the form and the
 * footer are 14 px apart, the form 10 px more. The guarded pair's wrap gap is `components.css` `.guarded{gap:12px}`.
 */
export const LOGIN_FORM = "mt-[10px] grid gap-[14px]";
/** The guarded pair's row and column gap are both 12 px (the kit draws 12 across and 4 down; D2 draws 12 both ways). */
export const LOGIN_ACT = "[&>span]:gap-y-3";

/**
 * `C-1` (BANK-V2) and `RESET-PASSWORD` R2: **the bank's door, as a frame.** The dark Hakili panel beside a white card and the
 * footer sentence are one drawing under every screen that stands before the session: the sign-in, « Mot de passe oublié » and
 * the new-password page a reset link lands on (frames 100 to 109). Taken out of `Connexion` so the three share ONE panel and
 * none of them can drift from the other two; the test ids are the ones `Connexion` has always carried.
 *
 * The panel goes under 1 100 px, where the card keeps the mark alone. **`SIGNIN-VEILLEUR` (D2 frames 70, 100 to 109):** the
 * veilleur at rest (no badge, no word) stands in the panel's middle, between the wordmark and the sentence, on all three
 * pages at once because the panel is this one component; it reads while the sign-in is in flight (`reading`).
 */
export function LoginShell(props: {
  readonly children: ReactNode;
  /** The sign-in is in flight: the face reads. */
  readonly reading?: boolean;
}): React.JSX.Element {
  return (
    <main
      className={classes(
        // D2 `screens.css:39` and `:56`: `.login{min-height:min(100vh,860px);display:grid;grid-template-columns:minmax(0,1.05fr) minmax(0,1fr)}` from 1 100 px.
        "flex min-h-[min(100vh,860px)] flex-col antialiased min-[1100px]:grid min-[1100px]:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)]",
        BANK_FONT,
        BANK_TEXT,
      )}
      data-testid="bank-login-page"
    >
      <aside
        data-testid="bank-login-panneau"
        className={classes(
          // D2 `screens.css:48`, under 1 100 px: `.lpanel{padding:28px;flex-direction:row;align-items:center;flex-wrap:wrap}`, the panel is a
          // band above the card (wordmark left, the veilleur beside it, no sentence); `:40`, from 1 100 px: `padding:48px 56px;
          // flex-direction:column;gap:18px`, the left half, the face between the wordmark and the sentence. `:55`/`:58`: the face
          // is a 300 px square in a `flex:1` cell, so the band is 28 + 300 + 28 = 356 px tall.
          "flex flex-wrap items-center gap-[18px] p-7 min-[1100px]:flex-col min-[1100px]:flex-nowrap min-[1100px]:items-stretch min-[1100px]:px-14 min-[1100px]:py-12",
          // D2 `screens.css:40` `.lpanel{background:linear-gradient(180deg,#121C45,#0B1230)}`: the bay's two grounds, not the flat first one.
          "bg-[linear-gradient(180deg,var(--hk-bay-0),var(--hk-bay-1))]",
          BANK_TEXT_BAY,
        )}
      >
        <div data-testid="bank-login-marque" className="flex items-center gap-[18px] min-[1100px]:block">
          <HakiliMark size={40} />
          <p className="m-0 text-[22px] font-semibold tracking-[0.2px] min-[1100px]:mt-[18px]">
            hakili{" "}
            <span
              className={classes("text-xl font-medium", BANK_TEXT_BAY_2)}
              data-testid="bank-login-wordmark-bank"
            >
              {t("bank.nav.bank")}
            </span>
          </p>
        </div>
        <div className="flex flex-1 items-center justify-center">
          <div className="w-[300px] shrink-0" data-testid="bank-login-veilleur">
            <HakiliMascot
              state={props.reading === true ? "reading" : "idle"}
              label={t("bank.bay.mascotte")}
            />
          </div>
        </div>
        <p className={classes("hidden text-lg leading-relaxed min-[1100px]:block", BANK_TEXT_BAY_2)}>
          {t("bank.login.panneauPhrase1")}
          <br />
          {t("bank.login.panneauPhrase2")}
        </p>
      </aside>
      {/* D2 `screens.css:42`: `.lcard{display:flex;flex-direction:column;justify-content:center;padding:48px clamp(24px,8vw,120px)}`, and `:43`
          `.lf{max-width:420px}`: the form block is 420 px at most, from the card's left padding. */}
      <section className="flex flex-1 flex-col justify-center bg-white px-[clamp(24px,8vw,120px)] py-12">
        <div className="flex w-full flex-col gap-[14px] [&>form]:max-w-[420px]">
          {props.children}
          <p
            className={classes("mt-6 flex items-center gap-2", BANK_FS_BODY, BANK_TEXT_2)}
            data-testid="bank-login-pied"
          >
            <Lock className="h-3.5 w-3.5" aria-hidden="true" />
            {t("bank.login.pied")}
          </p>
        </div>
      </section>
    </main>
  );
}

/** The quiet link under a form (« Retour à la connexion », « Mot de passe oublié ? »): a button, because it goes nowhere on its own. */
export function QuietLink(props: {
  readonly children: ReactNode;
  readonly onClick: () => void;
  readonly testId: string;
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={props.onClick}
      data-testid={props.testId}
      className={classes("self-start justify-self-start text-sm font-medium", LIEN_TEXTE)}
    >
      {props.children}
    </button>
  );
}

const swallow = (event: React.SyntheticEvent): void => {
  event.preventDefault();
  event.stopPropagation();
};

/**
 * The bank's INERT PRIMARY with no sentence of its own (`RESET-PASSWORD` R2, the designer's review of `95ef8568`, change 1).
 * `GuardedButton` always prints its reason beside the button (I19, DX-009); frames 109 and 104c say the reason ONCE, elsewhere
 * (the box above the field, the message under it), so the button is drawn as `GuardedButton` draws a refused one: the kit's
 * `data-kit="guarded"` hook, which `bank-theme.css` skins pale indigo, at its natural width, `aria-disabled` so it stays
 * focusable, its click and its two activating keys swallowed, and DESCRIBED by the element that says why.
 */
export function InertPrimary(props: {
  readonly children: ReactNode;
  readonly describedBy: string;
  readonly testId: string;
}): React.JSX.Element {
  return (
    <button
      type="submit"
      aria-disabled="true"
      aria-describedby={props.describedBy}
      onClick={swallow}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") swallow(event);
      }}
      onKeyUp={(event) => {
        if (event.key === "Enter" || event.key === " ") swallow(event);
      }}
      data-kit="guarded"
      data-size="lg"
      data-testid={props.testId}
      className="cursor-not-allowed rounded-xl border border-gray-200 bg-gray-50 px-4 py-2 text-sm font-medium whitespace-nowrap text-gray-500"
    >
      {props.children}
    </button>
  );
}
